import { useContext, useMemo } from 'react';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { ContentActionsProvider } from 'content-management-actions-lib/providers/ContentActionsProvider';
import { ContentUsageLimitsProvider, ContentUsageLimitsContext } from 'content-management-restrictions-lib';
import { AuthInfoProvider, ContentObjectTypeProvider, CrmObjectTypeIds } from 'content-management-data-lib';
import GlobalStyle from '../components/GlobalStyle';
import useActionSuccessUpdateHandlers from '../hooks/useActionSuccessUpdateHandlers';
import ContentPlatformPropertiesConfigurationProvider from './ContentPlatformPropertiesConfigurationProvider';
import FrameworkListingLibProviderWithContentConfig from './FrameworkListingLibProviderWithContentConfig';
import TrackerPropsProvider from './TrackerPropsProvider';
import ContentPlatformListingConfigurationProvider from './ContentPlatformListingConfigurationProvider';
import { CampaignsProvider } from 'campaigns-lib/campaignsPlatform';
import { useCmsUserSettings } from '../hooks/useCmsUserSettings';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BlockingDataSwitch = ({
  children,
  hasViews
}) => {
  const {
    loading
  } = useCmsUserSettings({
    skip: hasViews
  });
  return !hasViews && loading ? /*#__PURE__*/_jsx(LoadingSpinner, {}) : /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};
const WithContentUsageLimitsProvider = ({
  limitNameToLimitTypeMap = {},
  shouldFetchUsageLimits,
  contentObjectType,
  contentGroupObjectType,
  children
}) => {
  const contentUsageLimitsContext = useContext(ContentUsageLimitsContext);
  if (!contentUsageLimitsContext) {
    return /*#__PURE__*/_jsx(ContentUsageLimitsProvider, {
      limitNameToLimitTypeMap: limitNameToLimitTypeMap,
      shouldFetchUsageLimits: shouldFetchUsageLimits,
      contentObjectType: contentObjectType,
      contentGroupObjectType: contentGroupObjectType,
      children: children
    });
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};
const ContentListingLibProvider = ({
  crmObjectTypeId = CrmObjectTypeIds.CONTENT,
  contentGroupObjectType,
  contentObjectType,
  hasViews,
  savedViewsNamespace,
  currentViewId,
  onViewIdChange,
  trackerProps,
  defaultTableColumns,
  customTableColumnUserSettingsKey,
  filterValidationConfig: maybeFilterValidationConfig,
  hiddenFilter,
  emptyStateFilter,
  actionsConfigOptions,
  authInfo,
  limitNameToLimitTypeMap = {},
  shouldFetchUsageLimits,
  propertyFilterConfigs,
  propertyOverrides,
  skipQuickfetchContentByViewOptimization,
  defaultViewId,
  children
}) => {
  const filterValidationConfig = useMemo(() => {
    var _maybeFilterValidatio, _maybeFilterValidatio2, _maybeFilterValidatio3;
    const defaultIsFilterAvailableForUser = () => true;
    const defaultGetCorrectedAndAdditionalFiltersMap = __requestOptions => {
      return {};
    };
    const defaultIsAvailableAsFilterOnly = () => false;
    if (maybeFilterValidationConfig === undefined) {
      return {
        getCorrectedAndAdditionalFiltersMap: defaultGetCorrectedAndAdditionalFiltersMap,
        isFilterAvailableForUser: defaultIsFilterAvailableForUser,
        isAvailableAsFilterOnly: defaultIsAvailableAsFilterOnly
      };
    }
    return {
      getCorrectedAndAdditionalFiltersMap: (_maybeFilterValidatio = maybeFilterValidationConfig.getCorrectedAndAdditionalFiltersMap) !== null && _maybeFilterValidatio !== void 0 ? _maybeFilterValidatio : defaultGetCorrectedAndAdditionalFiltersMap,
      isFilterAvailableForUser: (_maybeFilterValidatio2 = maybeFilterValidationConfig.isFilterAvailableForUser) !== null && _maybeFilterValidatio2 !== void 0 ? _maybeFilterValidatio2 : defaultIsFilterAvailableForUser,
      isAvailableAsFilterOnly: (_maybeFilterValidatio3 = maybeFilterValidationConfig.isAvailableAsFilterOnly) !== null && _maybeFilterValidatio3 !== void 0 ? _maybeFilterValidatio3 : defaultIsAvailableAsFilterOnly
    };
  }, [maybeFilterValidationConfig]);
  return /*#__PURE__*/_jsx(ContentObjectTypeProvider, {
    contentObjectType: contentObjectType,
    contentGroupObjectType: contentGroupObjectType,
    children: /*#__PURE__*/_jsx(ContentPlatformListingConfigurationProvider, {
      hasViews: hasViews,
      savedViewsNamespace: savedViewsNamespace,
      defaultTableColumns: defaultTableColumns,
      customTableColumnUserSettingsKey: customTableColumnUserSettingsKey,
      crmObjectTypeId: crmObjectTypeId,
      children: /*#__PURE__*/_jsxs(AuthInfoProvider, {
        authInfo: authInfo,
        children: [/*#__PURE__*/_jsx(TrackerPropsProvider, {
          trackerProps: trackerProps,
          children: /*#__PURE__*/_jsx(CampaignsProvider, {
            children: /*#__PURE__*/_jsx(WithContentUsageLimitsProvider, {
              limitNameToLimitTypeMap: limitNameToLimitTypeMap,
              shouldFetchUsageLimits: shouldFetchUsageLimits,
              contentObjectType: contentObjectType,
              contentGroupObjectType: contentGroupObjectType,
              children: /*#__PURE__*/_jsx(ContentPlatformPropertiesConfigurationProvider, {
                filterValidationConfig: filterValidationConfig,
                contentGroupObjectType: contentGroupObjectType,
                propertyFilterConfigs: propertyFilterConfigs,
                propertyOverrides: propertyOverrides,
                children: /*#__PURE__*/_jsx(FrameworkListingLibProviderWithContentConfig, {
                  currentViewId: currentViewId,
                  onViewIdChange: onViewIdChange,
                  filterValidationConfig: filterValidationConfig,
                  hiddenFilter: hiddenFilter,
                  emptyStateFilter: emptyStateFilter,
                  skipQuickfetchContentByViewOptimization: skipQuickfetchContentByViewOptimization,
                  defaultViewId: defaultViewId,
                  children: /*#__PURE__*/_jsx(BlockingDataSwitch, {
                    hasViews: hasViews,
                    children: /*#__PURE__*/_jsx(ContentActionsProvider, Object.assign({}, actionsConfigOptions, {
                      useActionSuccessUpdateHandlers: useActionSuccessUpdateHandlers,
                      children: children
                    }))
                  })
                })
              })
            })
          })
        }), /*#__PURE__*/_jsx(GlobalStyle, {})]
      })
    })
  });
};
export default ContentListingLibProvider;